import { useMemo, useState, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import type { PerformanceRow } from '@/lib/types';
import { ZONE_LABELS, classNames } from '@/lib/utils';
import { useChartTheme } from '@/components/aperturas/shared';
import { fmt } from '@/components/reviews/shared';
import CorrelacionNota from '@/components/analytics/CorrelacionNota';

/**
 * Gráficas de rendimiento del negocio sobre las filas de Performance.
 *
 * Todas giran alrededor del RevPAR (€ por noche disponible = ADR × ocupación):
 * es la única métrica que permite comparar un estudio de Barbate con un ático
 * de Zahara sin que el tamaño ni el calendario hagan trampas. El objetivo no es
 * describir el mes sino decidir: a quién subir el precio el lunes, qué zona
 * rinde de verdad y de cuántos pisos depende la facturación.
 */

interface Punto {
  nombre: string;
  zona: string;
  ocupacion: number;
  adr: number;
  revpar: number;
  noches: number;
  facturacion: number;
}

/** Mediana clásica; `null` si no hay valores. */
function mediana(valores: number[]): number | null {
  if (valores.length === 0) return null;
  const orden = [...valores].sort((a, b) => a - b);
  const mitad = Math.floor(orden.length / 2);
  return orden.length % 2 === 1 ? orden[mitad] : (orden[mitad - 1] + orden[mitad]) / 2;
}

/** Nombre de piso recortado para ejes estrechos; el tooltip lo da completo. */
function corto(nombre: string, max = 14): string {
  return nombre.length > max ? `${nombre.slice(0, max - 1)}…` : nombre;
}

/** Tarjeta con el título de una línea y el subtítulo que dice qué decisión ayuda a tomar. */
function Tarjeta({ titulo, subtitulo, children }: { titulo: string; subtitulo: string; children: ReactNode }) {
  return (
    <div className="card p-4 sm:p-5">
      <h3 className="text-sm font-semibold truncate">{titulo}</h3>
      <p className="text-[12px] text-sereno-dark/50 leading-snug mt-0.5 mb-3">{subtitulo}</p>
      {children}
    </div>
  );
}

function Vacio({ texto = 'Sin datos que pintar en este periodo.' }: { texto?: string }) {
  return <p className="py-10 text-center text-sm text-sereno-dark/40">{texto}</p>;
}

/** Envoltorio del tooltip, mismo estilo que el de CorrelacionNota. */
function CajaTooltip({ children }: { children: ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-lg px-2.5 py-2 text-xs shadow-lg">
      {children}
    </div>
  );
}

/**
 * Los cuatro cuadrantes del mapa de precios, en orden de lectura. La posición
 * (esquina) solo se usa en pantallas anchas; en móvil se listan como leyenda.
 */
const CUADRANTES = [
  { posicion: 'top-2 right-3 text-right', etiqueta: 'Llenos y caros' },
  { posicion: 'top-2 left-14', etiqueta: 'Caros y vacíos: bajar precio o mejorar' },
  { posicion: 'bottom-10 right-3 text-right', etiqueta: 'Se llenan baratos: subir precio' },
  { posicion: 'bottom-10 left-14', etiqueta: 'Revisar a fondo' },
];

export default function GraficasRendimiento({ rows }: { rows: PerformanceRow[] }) {
  const chart = useChartTheme();
  const [zona, setZona] = useState<string>('todas');

  // Las zonas y su color salen de TODAS las filas, no de las filtradas: así
  // Zahara es del mismo color con el filtro puesto que sin él.
  const zonas = useMemo(
    () => [...new Set(rows.map((r) => r.zone || 'otro'))].sort(),
    [rows]
  );
  const colorDeZona = (z: string) => chart.palette[zonas.indexOf(z) % chart.palette.length];

  // Si al cambiar de periodo la zona elegida desaparece de las filas, el
  // filtro dejaría todo en «sin datos» sin ningún chip activo que lo explique:
  // en ese caso se cae a «todas» en vez de dejar al gestor atrapado.
  const zonaActiva = zona === 'todas' || zonas.includes(zona) ? zona : 'todas';

  const { puntos, excluidas } = useMemo(() => {
    const validas = rows.filter((r) => r.noches > 0);
    const filtradas =
      zonaActiva === 'todas' ? validas : validas.filter((r) => (r.zone || 'otro') === zonaActiva);
    const puntos: Punto[] = filtradas.map((r) => ({
      nombre: r.property,
      zona: r.zone || 'otro',
      ocupacion: r.ocupacion,
      adr: r.adr,
      revpar: (r.adr * r.ocupacion) / 100,
      noches: r.noches,
      facturacion: r.facturacion,
    }));
    return { puntos, excluidas: rows.length - validas.length };
  }, [rows, zonaActiva]);

  // Con el mismo corte de noches > 0 que el resto: así la nota al pie de
  // «quedan fuera de todas las gráficas» es verdad también para la de la nota.
  const filasFiltradas = useMemo(
    () =>
      rows.filter(
        (r) => r.noches > 0 && (zonaActiva === 'todas' || (r.zone || 'otro') === zonaActiva)
      ),
    [rows, zonaActiva]
  );

  const medOcupacion = mediana(puntos.map((p) => p.ocupacion));
  const medAdr = mediana(puntos.map((p) => p.adr));

  const ranking = useMemo(() => [...puntos].sort((a, b) => b.revpar - a.revpar), [puntos]);

  /**
   * Agregados por zona ponderados por noches DISPONIBLES, no media simple de
   * RevPARs: la media simple pesa igual un piso que aporta 20 noches al mercado
   * que uno que aporta 200, con lo que los pisos vacíos (RevPAR bajo, pocas
   * noches disponibles medidas) tirarían del número de su zona hacia donde no
   * toca. Sumando ingreso de alojamiento (≈ ADR × noches vendidas) y dividiendo
   * por las noches disponibles reales (noches / ocupación) el número de zona es
   * el que vería un inversor con la zona entera.
   */
  const porZona = useMemo(() => {
    const acum = new Map<string, { ingreso: number; disponibles: number; vendidas: number }>();
    for (const p of puntos) {
      // Con ocupación 0 no se pueden reconstruir las noches disponibles
      // (división por cero), así que el piso queda fuera del agregado.
      if (p.ocupacion <= 0) continue;
      const disponibles = p.noches / (p.ocupacion / 100);
      const previo = acum.get(p.zona) ?? { ingreso: 0, disponibles: 0, vendidas: 0 };
      acum.set(p.zona, {
        ingreso: previo.ingreso + p.adr * p.noches,
        disponibles: previo.disponibles + disponibles,
        vendidas: previo.vendidas + p.noches,
      });
    }
    return [...acum.entries()]
      .filter(([, v]) => v.disponibles > 0)
      .map(([z, v]) => ({
        zona: z,
        etiqueta: ZONE_LABELS[z] || z,
        revpar: v.ingreso / v.disponibles,
        ocupacion: (v.vendidas / v.disponibles) * 100,
      }))
      .sort((a, b) => b.revpar - a.revpar);
  }, [puntos]);

  const pareto = useMemo(() => {
    const orden = [...puntos].sort((a, b) => b.facturacion - a.facturacion);
    const total = orden.reduce((s, p) => s + p.facturacion, 0);
    if (total <= 0) return { filas: [], frase: null as string | null };
    let acumulado = 0;
    let pisosPara50: number | null = null;
    const filas = orden.map((p, i) => {
      acumulado += p.facturacion;
      const pct = (acumulado / total) * 100;
      if (pisosPara50 === null && pct >= 50) pisosPara50 = i + 1;
      return { nombre: p.nombre, zona: p.zona, facturacion: p.facturacion, acumulado: pct };
    });
    // Con un solo piso la frase diría «el 100 % genera el 50 %», que es
    // absurda y encima falsa: la concentración solo se enuncia si hay reparto.
    const frase =
      pisosPara50 === null || orden.length < 2
        ? null
        : `El ${fmt((pisosPara50 / orden.length) * 100, 0)} % de los pisos (${pisosPara50} de ${orden.length}) genera la mitad de la facturación.`;
    return { filas, frase };
  }, [puntos]);

  return (
    <div className="space-y-4">
      {/* Chips en vez de <select>: las zonas caben en una línea y así cambiar
          la vista entera de zona cuesta un toque en vez de dos. */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none -mx-1 px-1">
        {['todas', ...zonas].map((z) => (
          <button
            key={z}
            type="button"
            onClick={() => setZona(z)}
            aria-pressed={zonaActiva === z}
            className={classNames(
              'min-h-11 px-3 rounded-md text-sm whitespace-nowrap transition-colors',
              zonaActiva === z
                ? 'bg-sereno-dark text-white an-active'
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
            )}
          >
            {z === 'todas' ? 'Todas' : ZONE_LABELS[z] || z}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 1 · Mapa de precios */}
        <Tarjeta
          titulo="Mapa de precios"
          subtitulo="Ocupación contra precio por noche (ADR). Las medianas parten el plano en cuatro: dice a qué pisos subirles el precio el lunes."
        >
          {puntos.length === 0 ? (
            <Vacio />
          ) : (
            <>
              <div className="relative">
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart margin={{ top: 8, right: 12, bottom: 24, left: 4 }}>
                    <CartesianGrid stroke={chart.grid} />
                    <XAxis
                      type="number"
                      dataKey="ocupacion"
                      name="Ocupación"
                      domain={[0, 100]}
                      tickFormatter={(v: number) => `${Math.round(v)}%`}
                      tick={{ fontSize: 11, fill: chart.axis }}
                      label={{ value: 'Ocupación', position: 'insideBottom', offset: -14, fontSize: 11, fill: chart.axis }}
                    />
                    <YAxis
                      type="number"
                      dataKey="adr"
                      name="ADR"
                      domain={[0, 'auto']}
                      tickFormatter={(v: number) => `${Math.round(v)}€`}
                      tick={{ fontSize: 11, fill: chart.axis }}
                      width={44}
                    />
                    {/* El tamaño del punto son las noches vendidas: un piso que
                        mueve cien noches pesa más en la vista que uno de diez. */}
                    <ZAxis type="number" dataKey="noches" range={[40, 300]} name="Noches" />
                    {medOcupacion !== null && (
                      <ReferenceLine x={medOcupacion} stroke={chart.axis} strokeDasharray="4 4" strokeWidth={1} />
                    )}
                    {medAdr !== null && (
                      <ReferenceLine y={medAdr} stroke={chart.axis} strokeDasharray="4 4" strokeWidth={1} />
                    )}
                    <Tooltip
                      cursor={{ stroke: chart.axis, strokeDasharray: '3 3' }}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0].payload as Punto;
                        return (
                          <CajaTooltip>
                            <p className="font-semibold">{p.nombre}</p>
                            <p className="text-sereno-dark/60">{ZONE_LABELS[p.zona] || p.zona}</p>
                            <p>Ocupación {fmt(p.ocupacion, 0)} % · ADR {fmt(p.adr, 0)} €</p>
                            <p>
                              {fmt(p.revpar, 0)} € por noche disponible · {fmt(p.noches, 0)} noches
                            </p>
                          </CajaTooltip>
                        );
                      }}
                    />
                    <Scatter data={puntos} isAnimationActive={false}>
                      {puntos.map((p, i) => (
                        <Cell key={`${p.nombre}-${i}`} fill={colorDeZona(p.zona)} fillOpacity={0.75} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
                <div className="hidden sm:block absolute inset-0 pointer-events-none">
                  {CUADRANTES.map((c) => (
                    <span
                      key={c.etiqueta}
                      className={classNames('absolute max-w-[45%] text-[10px] leading-tight text-sereno-dark/40', c.posicion)}
                    >
                      {c.etiqueta}
                    </span>
                  ))}
                </div>
              </div>
              {/* En 343 px las etiquetas taparían los puntos: van de leyenda */}
              <div className="sm:hidden mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-sereno-dark/50">
                <span>↖ Caros y vacíos: bajar precio o mejorar</span>
                <span className="text-right">↗ Llenos y caros</span>
                <span>↙ Revisar a fondo</span>
                <span className="text-right">↘ Se llenan baratos: subir precio</span>
              </div>
            </>
          )}
        </Tarjeta>

        {/* 2 · Ranking RevPAR */}
        <Tarjeta
          titulo="Ranking por € por noche disponible (RevPAR)"
          subtitulo="Cada piso por lo que ingresa cada noche que está en el mercado. Ordena quién rinde de verdad, sin premiar a los grandes."
        >
          {ranking.length === 0 ? (
            <Vacio />
          ) : (
            /* La altura crece con los pisos para que cada barra siga legible,
               pero con techo: con ~50 pisos la tarjeta mediría 1.400 px y
               arrastraría la rejilla entera, así que el resto va con scroll
               dentro de la propia tarjeta. */
            <div className="max-h-[420px] overflow-y-auto overscroll-contain">
              <ResponsiveContainer width="100%" height={ranking.length * 28 + 40}>
                <BarChart data={ranking} layout="vertical" margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={chart.grid} horizontal={false} />
                  <XAxis
                    type="number"
                    tickFormatter={(v: number) => `${Math.round(v)}€`}
                    tick={{ fontSize: 11, fill: chart.axis }}
                  />
                  <YAxis
                    type="category"
                    dataKey="nombre"
                    width={110}
                    tickFormatter={(v: string) => corto(v)}
                    tick={{ fontSize: 10, fill: chart.axis }}
                    interval={0}
                  />
                  <Tooltip
                    cursor={{ fill: chart.cursor }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as Punto;
                      return (
                        <CajaTooltip>
                          <p className="font-semibold">{p.nombre}</p>
                          <p>{fmt(p.revpar, 0)} € por noche disponible</p>
                          <p className="text-sereno-dark/60">
                            ADR {fmt(p.adr, 0)} € · ocupación {fmt(p.ocupacion, 0)} %
                          </p>
                        </CajaTooltip>
                      );
                    }}
                  />
                  <Bar dataKey="revpar" isAnimationActive={false} radius={[0, 3, 3, 0]}>
                    {ranking.map((p, i) => (
                      <Cell key={`${p.nombre}-${i}`} fill={colorDeZona(p.zona)} />
                    ))}
                  </Bar>
              </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Tarjeta>

        {/* 3 y 4 · Agregados por zona: comparar una zona consigo misma no dice
            nada, así que con el filtro puesto estas tarjetas no se pintan y el
            grid recoloca las demás sin dejar hueco. */}
        {zonaActiva === 'todas' && (
          <Tarjeta
            titulo="€ por noche disponible (RevPAR) por zona"
            subtitulo="Ingreso de cada zona por noche que ofrece al mercado, ponderado por su oferta. Dice dónde rinde el negocio, no dónde hay más pisos."
          >
            {porZona.length === 0 ? (
              <Vacio />
            ) : (
              <ResponsiveContainer width="100%" height={porZona.length * 36 + 40}>
                <BarChart data={porZona} layout="vertical" margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={chart.grid} horizontal={false} />
                  <XAxis
                    type="number"
                    tickFormatter={(v: number) => `${Math.round(v)}€`}
                    tick={{ fontSize: 11, fill: chart.axis }}
                  />
                  <YAxis
                    type="category"
                    dataKey="etiqueta"
                    width={110}
                    tickFormatter={(v: string) => corto(v)}
                    tick={{ fontSize: 10, fill: chart.axis }}
                    interval={0}
                  />
                  <Tooltip
                    cursor={{ fill: chart.cursor }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as (typeof porZona)[number];
                      return (
                        <CajaTooltip>
                          <p className="font-semibold">{p.etiqueta}</p>
                          <p>{fmt(p.revpar, 0)} € por noche disponible</p>
                        </CajaTooltip>
                      );
                    }}
                  />
                  <Bar dataKey="revpar" isAnimationActive={false} radius={[0, 3, 3, 0]}>
                    {porZona.map((p) => (
                      <Cell key={p.zona} fill={colorDeZona(p.zona)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Tarjeta>
        )}

        {zonaActiva === 'todas' && (
          <Tarjeta
            titulo="Ocupación por zona"
            subtitulo="Noches vendidas sobre noches ofrecidas, ponderado por la oferta de cada zona. La media simple premiaría a los pisos vacíos."
          >
            {porZona.length === 0 ? (
              <Vacio />
            ) : (
              <ResponsiveContainer width="100%" height={porZona.length * 36 + 40}>
                <BarChart
                  data={[...porZona].sort((a, b) => b.ocupacion - a.ocupacion)}
                  layout="vertical"
                  margin={{ top: 0, right: 12, bottom: 0, left: 0 }}
                >
                  <CartesianGrid stroke={chart.grid} horizontal={false} />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={(v: number) => `${Math.round(v)}%`}
                    tick={{ fontSize: 11, fill: chart.axis }}
                  />
                  <YAxis
                    type="category"
                    dataKey="etiqueta"
                    width={110}
                    tickFormatter={(v: string) => corto(v)}
                    tick={{ fontSize: 10, fill: chart.axis }}
                    interval={0}
                  />
                  <Tooltip
                    cursor={{ fill: chart.cursor }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as (typeof porZona)[number];
                      return (
                        <CajaTooltip>
                          <p className="font-semibold">{p.etiqueta}</p>
                          <p>Ocupación {fmt(p.ocupacion, 1)} %</p>
                        </CajaTooltip>
                      );
                    }}
                  />
                  <Bar dataKey="ocupacion" isAnimationActive={false} radius={[0, 3, 3, 0]}>
                    {[...porZona].sort((a, b) => b.ocupacion - a.ocupacion).map((p) => (
                      <Cell key={p.zona} fill={colorDeZona(p.zona)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Tarjeta>
        )}

        {/* 5 · Pareto de la facturación */}
        <Tarjeta
          titulo="Concentración de la facturación"
          subtitulo="Pisos ordenados por lo que facturan y el acumulado encima. Mide cuánto depende el negocio de unos pocos pisos."
        >
          {pareto.filas.length === 0 ? (
            <Vacio />
          ) : (
            <>
              {pareto.frase && <p className="text-[12px] text-sereno-dark/60 mb-2">{pareto.frase}</p>}
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={pareto.filas} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={chart.grid} vertical={false} />
                  {/* Sin etiquetas en el eje X: con cuarenta pisos no caben ni
                      truncadas, y el tooltip ya da el nombre. */}
                  <XAxis dataKey="nombre" tick={false} axisLine={{ stroke: chart.grid }} height={6} />
                  <YAxis
                    yAxisId="euros"
                    tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v)))}
                    tick={{ fontSize: 11, fill: chart.axis }}
                    width={40}
                  />
                  <YAxis
                    yAxisId="pct"
                    orientation="right"
                    domain={[0, 100]}
                    tickFormatter={(v: number) => `${v}%`}
                    tick={{ fontSize: 11, fill: chart.axis }}
                    width={36}
                  />
                  <Tooltip
                    cursor={{ fill: chart.cursor }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as (typeof pareto.filas)[number];
                      return (
                        <CajaTooltip>
                          <p className="font-semibold">{p.nombre}</p>
                          <p>{fmt(p.facturacion, 0)} € facturados</p>
                          <p className="text-sereno-dark/60">
                            {fmt(p.acumulado, 0)} % acumulado hasta aquí
                          </p>
                        </CajaTooltip>
                      );
                    }}
                  />
                  <Bar yAxisId="euros" dataKey="facturacion" isAnimationActive={false} radius={[2, 2, 0, 0]}>
                    {pareto.filas.map((p, i) => (
                      <Cell key={`${p.nombre}-${i}`} fill={colorDeZona(p.zona)} />
                    ))}
                  </Bar>
                  <Line
                    yAxisId="pct"
                    type="monotone"
                    dataKey="acumulado"
                    stroke={chart.bar}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </>
          )}
        </Tarjeta>

        {/* 6 · Nota y facturación: el componente ya existe y trae su tarjeta */}
        <CorrelacionNota rows={filasFiltradas} />
      </div>

      {excluidas > 0 && (
        <p className="text-[11px] text-sereno-dark/40">
          {excluidas === 1
            ? '1 alojamiento sin noches vendidas queda fuera de todas las gráficas.'
            : `${excluidas.toLocaleString('es-ES')} alojamientos sin noches vendidas quedan fuera de todas las gráficas.`}
        </p>
      )}
    </div>
  );
}
